import { LucideIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StepHeader } from "./StepHeader";

interface TrainingDetailsStepProps {
  icon: LucideIcon;
  title: string;
  description: string;
  daysPerWeek: string;
  setDaysPerWeek: (value: string) => void;
  timePerSession: string;
  setTimePerSession: (value: string) => void;
  preferredTrainingTime: string;
  setPreferredTrainingTime: (value: string) => void;
  equipment: string;
  setEquipment: (value: string) => void;
  injuries: string;
  setInjuries: (value: string) => void;
  dietPreferences: string;
  setDietPreferences: (value: string) => void;
}

export function TrainingDetailsStep({
  icon,
  title,
  description,
  daysPerWeek,
  setDaysPerWeek,
  timePerSession,
  setTimePerSession,
  preferredTrainingTime,
  setPreferredTrainingTime,
  equipment,
  setEquipment,
  injuries,
  setInjuries,
  dietPreferences,
  setDietPreferences,
}: TrainingDetailsStepProps) {
  return (
    <div className="w-full max-w-sm animate-fade-in space-y-4 max-h-[60vh] overflow-y-auto px-1 -mx-1">
      <StepHeader icon={icon} title={title} description={description} />

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="daysPerWeek" className="text-xs">Days/Week</Label>
            <Select value={daysPerWeek} onValueChange={setDaysPerWeek}>
              <SelectTrigger className="bg-secondary/30 border-border/50 h-9">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 days</SelectItem>
                <SelectItem value="3">3 days</SelectItem>
                <SelectItem value="4">4 days</SelectItem>
                <SelectItem value="5">5 days</SelectItem>
                <SelectItem value="6">6 days</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="timePerSession" className="text-xs">Time/Session</Label>
            <Select value={timePerSession} onValueChange={setTimePerSession}>
              <SelectTrigger className="bg-secondary/30 border-border/50 h-9">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 min</SelectItem>
                <SelectItem value="45">45 min</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2+ hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="preferredTrainingTime" className="text-xs">Preferred Training Time</Label>
          <Select value={preferredTrainingTime} onValueChange={setPreferredTrainingTime}>
            <SelectTrigger className="bg-secondary/30 border-border/50 h-9">
              <SelectValue placeholder="When do you usually train?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="early-morning">Early Morning (5-7 AM)</SelectItem>
              <SelectItem value="morning">Morning (7-10 AM)</SelectItem>
              <SelectItem value="midday">Midday (10 AM - 1 PM)</SelectItem>
              <SelectItem value="afternoon">Afternoon (1-5 PM)</SelectItem>
              <SelectItem value="evening">Evening (5-8 PM)</SelectItem>
              <SelectItem value="night">Night (8 PM+)</SelectItem>
              <SelectItem value="varies">It varies</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="equipment" className="text-xs">Training Setup</Label>
          <Select value={equipment} onValueChange={setEquipment}>
            <SelectTrigger className="bg-secondary/30 border-border/50 h-9">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="full-gym">Full Gym Access</SelectItem>
              <SelectItem value="home-full">Home Gym (Rack, Barbell, Bench)</SelectItem>
              <SelectItem value="home-basic">Home Basic (Dumbbells, Bench)</SelectItem>
              <SelectItem value="home-minimal">Home Minimal (Dumbbells Only)</SelectItem>
              <SelectItem value="calisthenics">Calisthenics / Bodyweight</SelectItem>
              <SelectItem value="bands-kettlebells">Resistance Bands & Kettlebells</SelectItem>
              <SelectItem value="no-equipment">No Equipment</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="injuries" className="text-xs">Injuries / Limitations (optional)</Label>
          <Input
            id="injuries"
            type="text"
            placeholder="e.g., bad knee, shoulder issues"
            value={injuries}
            onChange={(e) => setInjuries(e.target.value)}
            className="bg-secondary/30 border-border/50 h-9"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="dietPreferences" className="text-xs">Diet Preferences / Restrictions (optional)</Label>
          <Input
            id="dietPreferences"
            type="text"
            placeholder="e.g., vegan, keto, no dairy"
            value={dietPreferences}
            onChange={(e) => setDietPreferences(e.target.value)}
            className="bg-secondary/30 border-border/50 h-9"
          />
        </div>
      </div>
    </div>
  );
}
